import * as React from "react";
import PropTypes from "prop-types";
import { useLocation } from "react-router-dom";
import { alpha } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import DeleteIcon from "@mui/icons-material/Delete";
import FilterListIcon from "@mui/icons-material/FilterList";
import { visuallyHidden } from "@mui/utils";
import { useState, useEffect } from "react";
import {
  getBackend,
  deleteBackend,
  deleteBackendBulk,
} from "../../api/backend";
import ItemForm from "../ItemForm";
import ConfirmationDialog from "../ConfirmationDialog";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import { useAuth } from "../../context/AuthContext";
import ColorBox from "../ColorBox";
import { CircularProgress, Alert } from "@mui/material";

export default function ItemList({
  fields,
  tableName,
  extraFieldInstances,
  hasCheckbox,
  objectName,
  canEdit = true,
  routeAfterCreate = false,
  tabledescription,
}) {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [openForm, setOpenForm] = useState(false);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [itemsToDelete, setItemsToDelete] = useState([]);
  const [order, setOrder] = React.useState("desc");
  const [orderBy, setOrderBy] = React.useState("internal_number");
  const [selected, setSelected] = React.useState([]);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [filterTerm, setFilterTerm] = useState("");
  const [showFilter, setShowFilter] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [, setOpenSnackbar] = useState(false);

  const location = useLocation();
  const hasPermission = (user, objectName = "task instance") => {
    if (user?.groups) {
      return {
        add: user.groups.some((group) =>
          user.group_permissions[group]?.includes(`Can add ${objectName}`)
        ),
        change: user.groups.some((group) =>
          user.group_permissions[group]?.includes(`Can change ${objectName}`)
        ),
        delete: user.groups.some((group) =>
          user.group_permissions[group]?.includes(`Can delete ${objectName}`)
        ),
      };
    }
  };

  const permissions = React.useMemo(
    () => hasPermission(user, objectName),
    [user, objectName]
  );

  const handleFilterChange = (event) => {
    setFilterTerm(event.target.value);
    if (
      location.pathname === "/work-orders" ||
      location.pathname === "/machines"
    ) {
      const filters = JSON.parse(sessionStorage.getItem("filters")) || {};
      filters[location.pathname] = event.target.value;
      sessionStorage.setItem("filters", JSON.stringify(filters));
    }
  };

  const handleToggleFilter = () => {
    setShowFilter(!showFilter);
  };

  const filteredRows = React.useMemo(() => {
    return rows.filter((row) => {
      return fields.some((field) => {
        return row[field.key]
          ?.toString()
          .toLowerCase()
          .includes(filterTerm.toLowerCase());
      });
    });
  }, [rows, fields, filterTerm]);

  const headCells = (fields) =>
    fields
      .filter((item) => item.displayList)
      .map((item) => ({
        id: item.key,
        numeric: item.numeric || false,
        disablePadding: item.disablePadding || false,
        label: item.label,
      }));

  function descendingComparator(a, b, orderBy) {
    const valueA = a[orderBy];
    const valueB = b[orderBy];

    if (!isNaN(valueA) && !isNaN(valueB)) {
      return valueB - valueA;
    }

    if (!isNaN(Date.parse(valueA)) && !isNaN(Date.parse(valueB))) {
      return new Date(valueB) - new Date(valueA);
    }

    if (typeof valueA === "string" && typeof valueB === "string") {
      return valueA < valueB ? -1 : valueA > valueB ? 1 : 0;
    }

    return 0;
  }

  function formatData(data) {
    return data.map((item) => {
      let rows = {};
      Object.keys(item).forEach((key) => {
        let field = fields.find((field) => field.key === key);
        let value = item[key];

        if (Array.isArray(value)) {
          // Handle array values
          rows[key] = value.map((v) => {
            if (field && field.options && field.options[v] !== undefined) {
              return field.options[v]; // Replace UUID with its corresponding label
            }
            return v; // Return value if no matching option is found
          });
        } else if (
          /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/.test(
            value
          ) &&
          key !== "uuid" && // Do not replace the primary UUID field
          field &&
          field.options &&
          field.options[value] !== undefined
        ) {
          rows[key] = field.options[value]; // Replace UUID with label from options
        } else {
          rows[key] = value; // Otherwise, retain the original value
        }
      });
      return rows;
    });
  }

  function getComparator(order, orderBy) {
    return order === "desc"
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  }

  function stableSort(array, comparator) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) {
        return order;
      }
      return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
  }

  function EnhancedTableHead(props) {
    const {
      fields,
      onSelectAllClick,
      order,
      orderBy,
      numSelected,
      rowCount,
      onRequestSort,
    } = props;
    const createSortHandler = (property) => (event) => {
      onRequestSort(event, property);
    };

    return (
      <TableHead>
        <TableRow>
          <TableCell padding="checkbox">
            {permissions?.delete && hasCheckbox && (
              <Checkbox
                color="primary"
                indeterminate={numSelected > 0 && numSelected < rowCount}
                checked={rowCount > 0 && numSelected === rowCount}
                onChange={onSelectAllClick}
                inputProps={{
                  "aria-label": "select all desserts",
                }}
              />
            )}
          </TableCell>
          {headCells(fields).map((headCell) => (
            <TableCell
              key={headCell.uuid}
              align={headCell.numeric ? "right" : "left"}
              padding={headCell.disablePadding ? "none" : "normal"}
              sortDirection={orderBy === headCell.id ? order : false}
            >
              <TableSortLabel
                active={orderBy === headCell.uuid}
                direction={orderBy === headCell.id ? order : "asc"}
                onClick={createSortHandler(headCell.id)}
              >
                {headCell.label}
                {orderBy === headCell.uuid ? (
                  <Box component="span" sx={visuallyHidden}>
                    {order === "desc"
                      ? "sorted descending"
                      : "sorted ascending"}
                  </Box>
                ) : null}
              </TableSortLabel>
            </TableCell>
          ))}
          <TableCell key={"add-button"} style={{ textAlign: "right" }}>
            {permissions?.delete && selected?.length > 0 && (
              <Button
                style={{ marginRight: 10 }}
                variant="contained"
                color="error"
                onClick={() => {
                  setItemsToDelete(selected);
                  setOpenConfirm(true);
                }}
              >
                <DeleteIcon />
              </Button>
            )}
            {permissions?.add && (
              <Button
                style={{ marginRight: 10 }}
                variant="contained"
                color="success"
                onClick={handleAdd}
              >
                <AddIcon />
              </Button>
            )}
          </TableCell>
        </TableRow>
      </TableHead>
    );
  }

  EnhancedTableHead.propTypes = {
    fields: PropTypes.array.isRequired, // fields prop added
    numSelected: PropTypes.number.isRequired,
    onRequestSort: PropTypes.func.isRequired,
    onSelectAllClick: PropTypes.func.isRequired,
    order: PropTypes.oneOf(["asc", "desc"]).isRequired,
    orderBy: PropTypes.string.isRequired,
    rowCount: PropTypes.number.isRequired,
  };

  function EnhancedTableToolbar(props) {
    const { numSelected, handleToggleFilter } = props;

    return (
      <Toolbar
        sx={{
          pl: { sm: 2 },
          pr: { xs: 1, sm: 1 },
          ...(numSelected > 0 && {
            bgcolor: (theme) =>
              alpha(
                theme.palette.primary.main,
                theme.palette.action.activatedOpacity
              ),
          }),
        }}
      >
        {numSelected > 0 && (
          <Typography
            sx={{ flex: "1 1 100%" }}
            color="inherit"
            variant="subtitle1"
            component="div"
          >
            {`${numSelected} ${
              numSelected > 1 ? "seleccionados" : "seleccionado"
            }`}
          </Typography>
        )}

        {numSelected > 0 ? (
          <Tooltip title="Delete">
            <IconButton onClick={() => handleOpenConfirm(selected)}>
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        ) : (
          <>
            <Tooltip title="Filter list">
              <IconButton onClick={handleToggleFilter}>
                <FilterListIcon />
              </IconButton>
            </Tooltip>
          </>
        )}
      </Toolbar>
    );
  }

  EnhancedTableToolbar.propTypes = {
    numSelected: PropTypes.number.isRequired,
    handleToggleFilter: PropTypes.func.isRequired,
  };

  const fetchRows = async () => {
    const data = await getBackend(tableName);
    setRows(formatData(data));
  };

  const handleBulkDelete = async () => {
    try {
      await deleteBackendBulk(tableName, itemsToDelete);
      setItemsToDelete([]);
      setSelected([]);
      setOpenConfirm(false);
      await fetchRows();
    } catch (error) {
      <Alert onClose={() => setOpenSnackbar(false)} severity="warning">
        {"No se pudieron eliminar los elementos"}
      </Alert>;
    }
  };

  const handleDelete = async () => {
    try {
      await deleteBackend(tableName, itemsToDelete[0].uuid);
      setItemsToDelete([]);
      setSelected([]);
      setOpenConfirm(false);
      await fetchRows();
    } catch (error) {
      console.error("Error deleting item: ", error);
    }
  };

  const handleOpenConfirm = (items) => {
    setItemsToDelete(items);
    setOpenConfirm(true);
  };

  const handleAdd = () => {
    setSelectedItem({});
    setOpenForm(true);
  };

  const handleCloseConfirm = () => {
    setItemsToDelete([]);
    setOpenConfirm(false);
  };

  const pickFieldsFromItem = (item, fieldsConfig) => {
    const keysToPick = fieldsConfig.map((field) => field.key);
    return keysToPick.reduce((result, key) => {
      if (item.hasOwnProperty(key)) {
        result[key] = item[key];
      }
      return result;
    }, {});
  };

  const handleEdit = (item) => {
    const pickFields = pickFieldsFromItem(item, fields);
    const formattedItem = formatItemForEdit(pickFields, fields);
    setSelectedItem(formattedItem);
    setOpenForm(true);
  };

  const formatItemForEdit = (item, fieldsConfig) => {
    return Object.keys(item).reduce((acc, key) => {
      const field = fieldsConfig.find((f) => f.key === key);
      if (field && field.type === "select") {
        // Find the UUID that corresponds to the displayed text
        const entry = Object.entries(field.options).find(
          ([_, value]) => value === item[key]
        );
        acc[key] = entry ? entry[0] : item[key]; // Use UUID if found, otherwise use the original value
      } else {
        acc[key] = item[key];
      }
      return acc;
    }, {});
  };

  const handleCloseForm = async () => {
    setOpenForm(false);
    await fetchRows();
  };

  const onStartUp = async () => {
    await fetchRows();
    setIsLoading(false);
  };

  useEffect(() => {
    if (
      location.pathname === "/work-orders" ||
      location.pathname === "/machines"
    ) {
      const filters = JSON.parse(sessionStorage.getItem("filters")) || {};
      const savedFilter = filters[location.pathname];
      if (savedFilter) {
        setFilterTerm(savedFilter);
      }
    }
  }, [location.pathname]);

  useEffect(() => {
    if (filterTerm) {
      setShowFilter(true);
      handleFilterChange({ target: { value: filterTerm } });
    } else {
      setShowFilter(false);
    }

    onStartUp();
  }, [filterTerm]);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelected = rows.map((n) => n.uuid);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, id) => {
    setSelected((prevSelected) => {
      const selectedIndex = prevSelected.indexOf(id);
      let newSelected = [];

      if (selectedIndex === -1) {
        newSelected = prevSelected.concat(id);
      } else {
        newSelected = prevSelected.filter((selectedId) => selectedId !== id);
      }
      return newSelected;
    });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleNavTo = (fieldConfig, row) => {
    if (fieldConfig.options) {
      const foundOption = Object.entries(fieldConfig.options).find(
        (option) => option[1] === row[fieldConfig.key]
      );
      if (foundOption) {
          window.open(`/${fieldConfig.navTo}/${foundOption[0]}`,'_blank');
          return;
        }
    }
    window.open(`/${fieldConfig.navTo}/${row.uuid}`,'_blank');
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const isSelected = (id) => selected.indexOf(id) !== -1;

  const visibleRows = React.useMemo(
    () =>
      stableSort(filteredRows, getComparator(order, orderBy)).slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
      ),
    [order, orderBy, page, rowsPerPage, filteredRows]
  );

  return isLoading ? (
    <CircularProgress />
  ) : (
    <Box sx={{ width: "100%" }}>
      <Paper elevation={4} sx={{ width: "100%", mb: 2 }}>
        <Box
          sx={{ display: "flex", alignItems: "center", position: "relative" }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexGrow: 1,
              ml: 1,
              mt: 2,
            }}
          >
            <IconButton onClick={handleToggleFilter}>
              <FilterListIcon />
            </IconButton>

            {showFilter && (
              <Box sx={{ ml: 2 }}>
                <TextField
                  label="Filtrar"
                  variant="outlined"
                  value={filterTerm}
                  onChange={handleFilterChange}
                  size="small"
                  sx={{ width: 300 }}
                />
              </Box>
            )}
          </Box>

          {/* Nombre de la tabla */}
          <Typography
            variant="h5"
            component="div"
            sx={{
              position: "absolute",
              top: "50%",
              left: "47.5%",
              transform: "translate(-50%, -50%)",
              textTransform: "uppercase",
              whiteSpace: "nowrap",
            }}
          >
            {tabledescription}
          </Typography>
        </Box>

        <TableContainer>
          <Table
            sx={{ minWidth: 750 }}
            aria-labelledby="tableTitle"
            size={"small"}
          >
            <EnhancedTableHead
              fields={fields}
              numSelected={selected?.length}
              order={order}
              orderBy={orderBy}
              onSelectAllClick={handleSelectAllClick}
              onRequestSort={handleRequestSort}
              rowCount={rows?.length}
            />
            <TableBody>
              {visibleRows.map((row, index) => {
                const isItemSelected = isSelected(row.uuid);
                const labelId = `enhanced-table-checkbox-${index}`;

                return (
                  <TableRow
                    hover
                    role="checkbox"
                    aria-checked={isItemSelected}
                    tabIndex={-1}
                    key={row.uuid}
                    selected={isItemSelected}
                  >
                    <TableCell padding="checkbox">
                      {permissions?.delete && hasCheckbox && (
                        <Checkbox
                          color="primary"
                          checked={isItemSelected}
                          onClick={(event) => handleClick(event, row.uuid)}
                          inputProps={{
                            "aria-labelledby": labelId,
                          }}
                        />
                      )}
                    </TableCell>
                    {fields.map(
                      (field) =>
                        !field?.includeInForm &&
                        field.displayList && (
                          <TableCell
                            key={field.key}
                            align={field.numeric ? "right" : "left"}
                          >
                            {field?.navTo ? (
                              Array.isArray(row[field.key]) ? (
                                row[field.key].map((value, i) => (
                                  <Button
                                    key={i}
                                    onClick={() =>
                                      handleNavTo(field, {
                                        ...row,
                                        [field.key]: value,
                                      })
                                    }
                                  >
                                    {value}
                                  </Button>
                                ))
                              ) : (
                                <Button onClick={() => handleNavTo(field, row)}>
                                  {`${row[field.key]}`}
                                </Button>
                              )
                            ) : field.type === "color-picker" ? (
                              <ColorBox color={row[field.key]} />
                            ) : Array.isArray(row[field.key]) ? (
                              row[field.key].join(", ")
                            ) : field.type === "bool" ? (
                              row[field.key] ? (
                                "Si"
                              ) : (
                                "No"
                              )
                            ) : row[field.key] === undefined ? null : (
                              <a>
                                {row[field.key]?.length <= 50
                                  ? row[field.key]
                                  : row[field.key]?.substr(0, 50) + "..."}
                              </a>
                            )}
                          </TableCell>
                        )
                    )}
                    <TableCell align="right">
                      {canEdit && (
                        <IconButton
                          onClick={() => handleEdit(row)}
                          style={{
                            visibility: permissions?.change
                              ? "visible"
                              : "hidden",
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                      )}
                      <IconButton
                        onClick={() => handleOpenConfirm([row])}
                        style={{
                          visibility: permissions?.delete
                            ? "visible"
                            : "hidden",
                        }}
                      >
                        <DeleteOutlineIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredRows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Filas por pagina"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}–${to} de ${count}`
          }
        />
      </Paper>

      {openForm && (
        <ItemForm
          onClose={handleCloseForm}
          item={selectedItem}
          tableName={tableName}
          extraFieldInstances={extraFieldInstances}
          fieldsConfig={fields}
          routeAfterCreate={routeAfterCreate}
          action={selectedItem.uuid ? "edit" : "add"}
        />
      )}
      <ConfirmationDialog
        openConfirm={openConfirm}
        handleCloseConfirm={handleCloseConfirm}
        handleDelete={
          typeof itemsToDelete?.[0] === "string"
            ? handleBulkDelete
            : handleDelete
        }
        itemsToDelete={itemsToDelete}
      />
    </Box>
  );
}

ItemList.propTypes = {
  fields: PropTypes.array.isRequired, // Ensure fields is passed as a prop
  tableName: PropTypes.string.isRequired,
  extraFieldInstances: PropTypes.array,
};
